import {Input, OnInit} from "@angular/core";
import {FormComponent} from "./model/form-component";
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {
    generateId,
    getOptionByKey,
    getValidatorByKey,
    hasOptionKey,
    hasValidatorKey, isTrue
} from "./model/form-component-utils";
import {OptionsEnum, PREDEFINED_OPTIONS, ValidatorsEnum} from "./model/dictionary";
import {DynamicFormValidators} from "./model/custom.validators";

export class DynamicComponent implements OnInit {

    @Input()
    component: FormComponent;

    @Input()
    data: any;

    @Input()
    formGroup: FormGroup;

    @Input()
    formControl: FormControl;

    label = '';
    // ** these are FormComponent.options that are implemented with default values
    hidden = false;
    readonly = false;
    appearance: string;
    noOfColumns: string; // sets the the columns in the grid layout
    // ** these are some FormComponent.validators that are used in UI
    isRequired = false;

    value: string = '';

    ngOnInit(): void {
        // init values
        if (!this.component.id) {
            this.component.id = generateId(this.component);
        }

        if (this.component.label) {
            this.label = this.component.label;
        }
        // init options
        this.hidden = isTrue(this.getOptionByKey(OptionsEnum.hidden));
        this.readonly = isTrue(this.getOptionByKey(OptionsEnum.readOnly));
        const defaultValueAppearance = PREDEFINED_OPTIONS.find(opt => opt.key === OptionsEnum.appearance).value;
        this.appearance = this.getOptionByKeyWithDefault(OptionsEnum.appearance, defaultValueAppearance);
        this.isRequired = isTrue(this.getValidatorByKey(ValidatorsEnum.required));
        const defaultValueGridCols = PREDEFINED_OPTIONS.find(opt => opt.key === OptionsEnum.gridCols).value;
        this.noOfColumns = this.getOptionByKeyWithDefault(OptionsEnum.gridCols, defaultValueGridCols);
        this.initFormControl();
    }

    getErrorMessage(): string {
        if (this.formControl.hasError(ValidatorsEnum.required)) {
            return 'The field "' + this.stripSpecialChars(this.label) + '" is required!';
        }
        if (this.formControl.hasError(ValidatorsEnum.pattern)) {
            return 'The field "' + this.stripSpecialChars(this.label) + '" does not match the pattern: ' + this.getValidatorByKey('pattern');
        }
        if (this.formControl.hasError(ValidatorsEnum.dateInFuture)) {
            return 'The date is not in future!';
        }
        if (this.formControl.hasError(ValidatorsEnum.dateInPast)) {
            return 'The date is not in past!';
        }
        if (this.formControl.hasError(ValidatorsEnum.email)) {
            return 'This is not a valid email!';
        }
        if (this.formControl.hasError(ValidatorsEnum.max)) {
            return 'The number should be lower than :' + this.getValidatorByKey(ValidatorsEnum.max);
        }
        if (this.formControl.hasError(ValidatorsEnum.min)) {
            return 'The number should be higher than :' + this.getValidatorByKey(ValidatorsEnum.min);
        }
        if (this.formControl.hasError(ValidatorsEnum.maxlength)) {
            return 'The value should have maximum ' + this.getValidatorByKey(ValidatorsEnum.maxlength) + ' chars';
        }
        if (this.formControl.hasError(ValidatorsEnum.minlength)) {
            return 'The value should have minimum ' + this.getValidatorByKey(ValidatorsEnum.minlength) + ' chars';
        }

        return '';
    }

    private initFormControl() {
        this.formControl = new FormControl('', this.createValidators());
        this.formGroup.addControl(this.component.id, this.formControl);

        const defaultValue = this.getOptionByKey(OptionsEnum.defaultValue);
        if (defaultValue != undefined) {
            this.formGroup.controls[this.component.id].setValue(defaultValue);
        }
    }

    private createValidators(): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (isTrue(this.getValidatorByKey(ValidatorsEnum.required))) {
            validators.push(Validators.required);
        }
        if (isTrue(this.getValidatorByKey(ValidatorsEnum.email))) {
            validators.push(Validators.email);
        }
        if (this.hasValidatorKey(ValidatorsEnum.max)) {
             validators.push(Validators.max(+this.getValidatorByKey(ValidatorsEnum.max)));
        }
        if (this.hasValidatorKey(ValidatorsEnum.min)) {
            validators.push(Validators.min(+this.getValidatorByKey(ValidatorsEnum.min)));
        }
        if (this.hasValidatorKey(ValidatorsEnum.maxlength)) {
            validators.push(Validators.maxLength(+this.getValidatorByKey(ValidatorsEnum.maxlength)));
        }
        if (this.hasValidatorKey(ValidatorsEnum.minlength)) {
            validators.push(Validators.minLength(+this.getValidatorByKey(ValidatorsEnum.minlength)));
        }
        if (this.hasValidatorKey(ValidatorsEnum.pattern)) {
            validators.push(Validators.pattern(this.getValidatorByKey(ValidatorsEnum.pattern)));
        }
        if (isTrue(this.getValidatorByKey(ValidatorsEnum.dateInFuture))) {
            validators.push(DynamicFormValidators.dateInFuture);
        }
        if (isTrue(this.getValidatorByKey(ValidatorsEnum.dateInPast))) {
            validators.push(DynamicFormValidators.dateInPast);
        }

        return validators;
    }

    public hasOptionKey(key: string): boolean {
        return hasOptionKey(this.component, key);
    }

    public hasValidatorKey(key: string): boolean {
        return hasValidatorKey(this.component, key);
    }

    public hasOptionValue(component: FormComponent, key: string, value: any): boolean {
        return getOptionByKey(component, key, undefined) === value;
    }

    public getOptionByKeyWithDefault(key: string, defaultValue: any): string {
        return getOptionByKey(this.component, key, defaultValue);
    }

    public getOptionByKey(key: string): any {
        return getOptionByKey(this.component, key, undefined);
    }

    private getValidatorByKey(key: string): any {
        return getValidatorByKey(this.component, key, undefined);
    }

    /**
     * Remove special terminating chars like: . : ?
     * @param label
     * @private
     */
    private stripSpecialChars(label: string) {
        if (label.trim().endsWith(':') || label.trim().endsWith('.') || label.trim().endsWith('?')) {
            return label.trim().substr(0, label.length - 1);
        }
        return label;
    }

    isHidden(component:FormComponent): boolean {
        return isTrue(getOptionByKey(component, 'hidden', false));
    }
}
